"use client";

import { type Listing } from '@/services/api';
import { apiClient } from '@/services/api/client';
import { EVENT_TYPES } from '@/utils/constants';
import { logEvent } from '@/utils/helpers/analyticsHelpers';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

interface PriceTrackingContextType {
  trackedListings: Set<string>;
  setTrackedListings: React.Dispatch<React.SetStateAction<Set<string>>>;
  addPriceTracking: (listing: Listing) => Promise<boolean>;
  removePriceTracking: (listingId: string) => Promise<boolean>;
  determineIfPriceTracked: (listingId: string) => boolean;
  loading: boolean;
}

const PriceTrackingContext = createContext<PriceTrackingContextType | undefined>(undefined);

export const PriceTrackingProvider = ({ children }: { children: React.ReactNode }) => {
  const [trackedListings, setTrackedListings] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchTrackedItems = React.useCallback(async () => {
    if (!user?._id) {
      setTrackedListings(new Set());
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get<{ alerts: Array<{ listing_id: string }> }>(
        `/price-alerts/user/${user._id}`
      );
      setTrackedListings(new Set(response.alerts.map(alert => alert.listing_id)));
    } catch (error) {
      console.error('Error fetching tracked items:', error);
      setTrackedListings(new Set());
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTrackedItems();
  }, [fetchTrackedItems]);

  const togglePriceTracking = async (listingId: string, listing?: Listing): Promise<boolean> => {
    if (!user?._id) return false;

    try {
      const response = await apiClient.post<{ status: string }>('/price-alerts/toggle', {
        userId: user._id,
        listingId
      });

      if (response.status === "added") {
        setTrackedListings(prev => new Set(prev).add(listingId));
        if (listing) {
          logEvent(user, EVENT_TYPES.NOTIFY_ADDED, {
            listing_id: listing._id,
            vendor: listing.vendor
          });
        }
        return true;
      }

      if (response.status === "removed") {
        setTrackedListings(prev => {
          const newSet = new Set(prev);
          newSet.delete(listingId);
          return newSet;
        });
        if (listing) {
          logEvent(user, EVENT_TYPES.NOTIFY_REMOVED, {
            listing_id: listingId
          });
        }
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error toggling price tracking:', error);
      return false;
    }
  };

  const addPriceTracking = async (listing: Listing): Promise<boolean> => {
    return await togglePriceTracking(listing._id, listing);
  };

  const removePriceTracking = async (listingId: string): Promise<boolean> => {
    return await togglePriceTracking(listingId);
  };

  const determineIfPriceTracked = (listingId: string): boolean => {
    if (!listingId || !user?._id) return false;
    return trackedListings.has(listingId);
  };

  const value: PriceTrackingContextType = {
    trackedListings,
    setTrackedListings,
    addPriceTracking,
    removePriceTracking,
    determineIfPriceTracked,
    loading,
  };

  return (
    <PriceTrackingContext.Provider value={value}>
      {children}
    </PriceTrackingContext.Provider>
  );
};

export const usePriceTracking = (): PriceTrackingContextType => {
  const context = useContext(PriceTrackingContext);
  if (!context) {
    throw new Error('usePriceTracking must be used within a PriceTrackingProvider');
  }
  return context;
};
